import { NgForOf } from '@angular/common';
import { Component, effect, input, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { IWidgetAttachments } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { FileUtilities } from '../../../core/services/file-utilities.service';
import { IsUrlValidator } from '../../../core/validators/is-url.validator';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ErrorMessagesComponent } from '../error-messages/error-messages.component';
import { ImageDialogComponent } from './image-dialog/image-dialog.component';
import { ItemAttachmentThumbnail, MemoryStoredWidgetAttachmentFile } from './image-upload.interface';
import { ImageUploadStore } from './image-upload.store';

@Component({
  imports: [
    NgForOf,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatLabel,
    MatButton,
    TranslatePipe,
    ErrorMessagesComponent,
    MatError,
  ],
  providers: [ImageUploadStore],
  selector: 'app-image-upload',
  styleUrl: './image-upload.component.scss',
  templateUrl: './image-upload.component.html',
})
export class ImageUploadComponent implements OnInit {
  private static readonly allowedImageMimeTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg'];

  @Input() formControl!: FormControl<MemoryStoredWidgetAttachmentFile[] | null>;
  public isFormSubmitted = input<boolean>(false);
  public canDeleteAttachments = input<boolean>(false);

  public urlControl: FormControl = new FormControl<string>('', {
    validators: [Validators.required, IsUrlValidator()],
  });
  public thumbnails: ItemAttachmentThumbnail[] = [];
  public files: MemoryStoredWidgetAttachmentFile[] = [];

  constructor(
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly store: ImageUploadStore,
    private readonly matDialog: MatDialog,
  ) {
    effect(() => {
      if (this.isFormSubmitted()) {
        this.syncThumbnailsAndValidators();
      }
    });
  }

  public ngOnInit(): void {
    this.listenUploadedAttachments();
    this.listenPreviewAttachment();
    this.handleInitialValue();
  }

  private listenPreviewAttachment(): void {
    this.store.previewAttachment$
      .pipe(filter((attachment): attachment is IWidgetAttachments => !!attachment?.base64))
      .subscribe((attachment: IWidgetAttachments) => {
        this.store.clearPreviewImage();

        const objectUrl: string = this.createObjectUrlFromBase64(
          attachment.base64,
          attachment.originalFileName,
          attachment.mimeType,
        );

        if (objectUrl) {
          this.openDialogWithUrl(objectUrl, true);
        }
      });
  }

  private listenUploadedAttachments(): void {
    this.store.uploadedAttachments$
      .pipe(filter((data): data is IWidgetAttachments[] => !!data))
      .subscribe((attachments: IWidgetAttachments[]) => {
        this.thumbnails = attachments.map((att: IWidgetAttachments) => this.mapAttachmentToThumbnail(att));

        this.files = attachments.map((att: IWidgetAttachments) => {
          const thumb: ItemAttachmentThumbnail | undefined = this.thumbnails.find((t) => t.id === att.id);

          return new MemoryStoredWidgetAttachmentFile(att.id, att.originalFileName, thumb?.filePath || '', null as any);
        });

        this.syncThumbnailsAndValidators();
      });
  }

  private mapAttachmentToThumbnail(att: IWidgetAttachments): ItemAttachmentThumbnail {
    let objectUrl = '';

    if (att.base64) {
      objectUrl = this.createObjectUrlFromBase64(att.base64, att.originalFileName, att.mimeType);
    }

    return new ItemAttachmentThumbnail(att.id, att.originalFileName, objectUrl, att.mimeType);
  }

  private createObjectUrlFromBase64(base64: string, fileName: string, mimeType: string): string {
    try {
      const base64Data: string = base64.includes(',') ? base64.split(',')[1] : base64;
      const u8array: Uint8Array = FileUtilities.fromBase64ToUint8Array(base64Data);
      const file: File = FileUtilities.fromUint8ArrayToFile(u8array, fileName, mimeType);

      return URL.createObjectURL(file);
    } catch (_error) {
      this.snackbarService.open(this.translate.instant('system.message.operationFailed'), ESnackbar.error);

      return '';
    }
  }

  private handleInitialValue(): void {
    if (this.isNumberArray(this.formControl.value)) {
      this.store.loadUploadedImages(this.formControl.value as number[]);
    } else if (this.isAttachmentFileArray(this.formControl.value)) {
      this.files = [...this.formControl.value];
      this.thumbnails = this.formControl.value.map(
        (file) => new ItemAttachmentThumbnail(file.id, file.originalFileName, file.url, file.file?.type || ''),
      );
    }
  }

  private isAttachmentFileArray(value: any): value is MemoryStoredWidgetAttachmentFile[] {
    return Array.isArray(value) && value.length > 0 && value[0] instanceof MemoryStoredWidgetAttachmentFile;
  }

  private isNumberArray(value: any): value is number[] {
    return Array.isArray(value) && value.length > 0 && typeof value[0] === 'number';
  }

  public async addUrl(): Promise<void> {
    const val: string = typeof this.urlControl.value === 'string' ? this.urlControl.value.trim() : '';

    if (!val || !this.isValidUrl(val)) {
      return;
    }

    try {
      const base64: string = await FileUtilities.fromImageUrlToBase64(val);
      const filename: string = val.split('/').pop() ?? 'image';
      const file: File = await FileUtilities.fromUrlToFile(val, filename);

      const thumb = new ItemAttachmentThumbnail(null, filename, base64, file.type);
      this.thumbnails.push(thumb);

      const objectUrl: string = URL.createObjectURL(file);
      this.files.push(new MemoryStoredWidgetAttachmentFile(null, filename, objectUrl, file));

      this.urlControl.setValue('');
      this.syncThumbnailsAndValidators();
    } catch (_error) {
      this.snackbarService.open(this.translate.instant('system.message.corsError'), ESnackbar.error);
    }
  }

  public async onFileSelected(event: Event): Promise<void> {
    const inputValue = event.target as HTMLInputElement;

    if (!inputValue.files?.length) {
      return;
    }

    const filesArray: File[] = Array.from(inputValue.files);

    for (const file of filesArray) {
      if (!ImageUploadComponent.allowedImageMimeTypes.includes(file.type)) {
        this.snackbarService.open(this.translate.instant('apiError.unsupportedImageFile'), ESnackbar.error);

        continue;
      }

      try {
        const base64DataUrl: string = await FileUtilities.fromFileToBase64(file);
        this.thumbnails.push(new ItemAttachmentThumbnail(null, file.name, base64DataUrl, file.type));

        const objectUrl: string = URL.createObjectURL(file);
        this.files.push(new MemoryStoredWidgetAttachmentFile(null, file.name, objectUrl, file));
      } catch (_error) {
        this.snackbarService.open(this.translate.instant('system.message.operationFailed'), ESnackbar.error);
      }
    }

    this.syncThumbnailsAndValidators();
    inputValue.value = '';
  }

  public onImageError(): void {
    this.snackbarService.open(this.translate.instant('system.message.operationFailed'), ESnackbar.error);
  }

  public openImageDialog(thumbnail: ItemAttachmentThumbnail): void {
    if (thumbnail.id === null) {
      this.openDialogWithUrl(thumbnail.filePath, false);

      return;
    }

    this.store.loadPreviewImage(thumbnail.id);
  }

  private openDialogWithUrl(imageUrl: string, shouldRevokeOnClose: boolean): void {
    this.dialog
      .open(ImageDialogComponent, {
        data: { cancelButtonText: this.translate.instant('button.close'), imageUrl },
        maxWidth: '100%',
        width: EDialogWidth.medium,
      })
      .afterClosed()
      .subscribe(() => {
        if (shouldRevokeOnClose) {
          URL.revokeObjectURL(imageUrl);
        }
      });
  }

  public onClickedImageRemove(index: number): void {
    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('widget.imageViewer.deleteDialog.content'),
        submitButtonText: this.translate.instant('button.remove'),
        title: this.translate.instant('widget.imageViewer.deleteDialog.title'),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.remove(index);
      }
    });
  }

  private remove(index: number): void {
    const thumbnail: ItemAttachmentThumbnail = this.thumbnails[index];

    if (thumbnail.id && this.canDeleteAttachments()) {
      this.store.deleteAttachment(thumbnail.id);
    }

    this.thumbnails.splice(index, 1);
    this.files.splice(index, 1);
    this.syncThumbnailsAndValidators();
  }

  private isValidUrl(url: string): boolean {
    const result: ValidationErrors | null = IsUrlValidator()({ value: url } as AbstractControl);

    return result === null;
  }

  private syncThumbnailsAndValidators(): void {
    this.generateObjectUrlsForThumbnails();

    this.formControl.setValue([...this.files]);

    const validators: ValidatorFn[] = this.thumbnails.length
      ? [IsUrlValidator()]
      : [Validators.required, IsUrlValidator()];

    this.urlControl.setValidators(validators);

    if (!this.thumbnails.length && this.isFormSubmitted()) {
      this.urlControl.markAsTouched();
    }

    this.urlControl.updateValueAndValidity({ emitEvent: false });
  }

  private generateObjectUrlsForThumbnails(): void {
    this.thumbnails = this.thumbnails.map((thumbnail) => {
      let { filePath } = thumbnail;

      if (filePath && filePath.startsWith('data:')) {
        try {
          const [, base64] = filePath.split(',');
          const file: File = FileUtilities.fromUint8ArrayToFile(
            FileUtilities.fromBase64ToUint8Array(base64),
            thumbnail.originalFileName,
            thumbnail.mimeType,
          );
          filePath = URL.createObjectURL(file);
        } catch (_error) {
          this.snackbarService.open(this.translate.instant('system.message.operationFailed'), ESnackbar.error);
        }
      }

      return new ItemAttachmentThumbnail(thumbnail.id, thumbnail.originalFileName, filePath, thumbnail.mimeType);
    });
  }
}
